import { QuotePaymentScheduleDueDateAnchorEnum } from '@/gen/cortado/v1/settings_pb'
import type { QuotePaymentSchedule } from '@/gen/cortado/v1/invoices_pb'
import { timestampToDateOnly } from '@/lib/date-utils'

export interface QuotePaymentScheduleDueFields {
  dueDate?: Date
  dueDateAnchor?: QuotePaymentScheduleDueDateAnchorEnum
  dueOffsetDays?: number
}

export const DEFAULT_DEPOSIT_DUE_DAYS = 7
export const MIN_DEPOSIT_DUE_DAYS = 0
export const MAX_DEPOSIT_DUE_DAYS = 90

export const DEFAULT_REMAINDER_DUE_DAYS = 14
export const MIN_REMAINDER_DUE_DAYS = 0
export const MAX_REMAINDER_DUE_DAYS = 365

export function normalizeDepositDueDays(days: number | undefined | null): number {
  if (days == null || days < MIN_DEPOSIT_DUE_DAYS || days > MAX_DEPOSIT_DUE_DAYS) {
    return DEFAULT_DEPOSIT_DUE_DAYS
  }
  return days
}

export function normalizeRemainderDueDays(days: number | undefined | null): number {
  if (days == null || days < MIN_REMAINDER_DUE_DAYS || days > MAX_REMAINDER_DUE_DAYS) {
    return DEFAULT_REMAINDER_DUE_DAYS
  }
  return days
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function addLocalDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return startOfLocalDay(result)
}

export function earliestEventDate(eventDates: Array<Date | undefined | null>): Date | undefined {
  const datedEvents = eventDates
    .filter((date): date is Date => date instanceof Date && !Number.isNaN(date.getTime()))
    .map(startOfLocalDay)
  if (datedEvents.length === 0) return undefined
  return datedEvents.reduce((min, date) => (date < min ? date : min), datedEvents[0])
}

export function resolveBeforeEventPreviewDueDate(
  now: Date,
  offsetDays: number,
  eventDates: Array<Date | undefined | null>,
): Date {
  const today = startOfLocalDay(now)
  const earliest = earliestEventDate(eventDates)
  if (!earliest) return today
  const candidate = addLocalDays(earliest, -offsetDays)
  return candidate < today ? today : candidate
}

const dueDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

export function normalizeDueDateAnchor(
  anchor: QuotePaymentScheduleDueDateAnchorEnum | undefined | null,
): QuotePaymentScheduleDueDateAnchorEnum {
  switch (anchor) {
    case QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_AFTER_ACCEPTANCE:
    case QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_BEFORE_EVENT:
    case QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_ABSOLUTE:
      return anchor
    default:
      return QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_ABSOLUTE
  }
}

export function formatQuotePaymentScheduleDue(
  schedule: QuotePaymentScheduleDueFields,
): string {
  const anchor = normalizeDueDateAnchor(schedule.dueDateAnchor)
  if (anchor === QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_AFTER_ACCEPTANCE) {
    const offset = schedule.dueOffsetDays ?? 0
    if (offset <= 0) return 'Due on acceptance'
    if (offset === 1) return 'Due 1 day after acceptance'
    return `Due ${offset} days after acceptance`
  }
  return schedule.dueDate ? `Due ${dueDateFormatter.format(schedule.dueDate)}` : 'Due date not set'
}

export function formatQuotePaymentScheduleDueFromProto(schedule: QuotePaymentSchedule): string {
  return formatQuotePaymentScheduleDue({
    dueDate: timestampToDateOnly(schedule.dueDate) ?? undefined,
    dueDateAnchor: schedule.dueDateAnchor,
    dueOffsetDays: schedule.dueOffsetDays,
  })
}

export function recomputeBeforeEventPreviewDueDates<T extends QuotePaymentScheduleDueFields>(
  schedules: T[],
  now: Date,
  eventDates: Array<Date | undefined | null>,
): T[] {
  return schedules.map((schedule) => {
    if (
      normalizeDueDateAnchor(schedule.dueDateAnchor)
      !== QuotePaymentScheduleDueDateAnchorEnum.QUOTE_PAYMENT_SCHEDULE_DUE_DATE_ANCHOR_BEFORE_EVENT
    ) {
      return schedule
    }
    return {
      ...schedule,
      dueDate: resolveBeforeEventPreviewDueDate(now, schedule.dueOffsetDays ?? 0, eventDates),
    }
  })
}
